// @flow
import { format } from "date-fns/format";
import { isAfter } from "date-fns/isAfter";
import { isBefore } from "date-fns/isBefore";
import { isSameDay } from "date-fns/isSameDay";
import { isValid } from "date-fns/isValid";
import { parse } from "date-fns/parse";
import { add } from "date-fns/add";
import { sub } from "date-fns/sub";
import { set } from "date-fns/set";
import { startOfMonth } from "date-fns/startOfMonth";
import { endOfMonth } from "date-fns/endOfMonth";
import { startOfWeek } from "date-fns/startOfWeek";
import { getHours } from "date-fns/getHours";
import { getMinutes } from "date-fns/getMinutes";
import { getDay } from "date-fns/getDay";
import { getWeek } from "date-fns/getWeek";

import { enGB, nl } from "date-fns/locale";
import { tz } from "@date-fns/tz";

import soft from "timezone-soft";

import {
  DATETIME_OFFSET_FORMAT,
  DEFAULT_FIRST_WEEK_CONTAINS_DATE,
  DEFAULT_WEEK_STARTS_ON,
  getEnabledLocales,
  getSetting,
  IS_GRAALJS,
  isIncludeTimeOffsetInDateTimes,
  ISO_DATE_FORMAT,
  ISO_DATETIME_FORMAT,
  ISO_TIME_FORMAT,
  ISO_TIMESTAMP_FORMAT,
} from "../../constants";

import { getCookie } from "../browser/Cookies";

type OffsetInfoInput = {
  abbr: string,
  offset: number,
  name: string,
};

export type OffsetInfo = {
  abbr: string,
  label: string,
  value: string,
};

/**
 * @hideconstructor
 */
class BaseDateTimeUtil {
  _isoFormat: string;
  _doOffsets: boolean;

  _timeZone: string =
    typeof Intl === "undefined"
      ? "Etc/UTC"
      : Intl.DateTimeFormat().resolvedOptions().timeZone || "Etc/UTC";

  constructor(isoFormat: string, doOffsets: boolean = false) {
    this._isoFormat = isoFormat;
    this._doOffsets = doOffsets;
  }

  /**
   * Indicates if offsets and timezones are relevant
   */
  get doOffsets(): boolean {
    return this._doOffsets ? isIncludeTimeOffsetInDateTimes() : false;
  }

  /**
   * Returns the options defined in settings
   */
  getOptions(): {
    weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6,
    firstWeekContainsDate: 1 | 2 | 3 | 4 | 5 | 6 | 7,
  } {
    // indicates on what day the week starts
    const weekStartsOn = getSetting(
      "CALENDAR_WEEK_STARTS_ON",
      DEFAULT_WEEK_STARTS_ON,
    );

    // indicates which date indicates the first week of the year
    const firstWeekContainsDate = getSetting(
      "CALENDAR_FIRST_WEEK_CONTAINS_DATE",
      DEFAULT_FIRST_WEEK_CONTAINS_DATE,
    );

    return { weekStartsOn, firstWeekContainsDate };
  }

  /**
   */
  getIsoFormat(): string {
    if (this.doOffsets) {
      return this._isoFormat + DATETIME_OFFSET_FORMAT;
    }
    return this._isoFormat;
  }

  /**
   */
  convertFormat(javaFormat: string): string {
    if (!javaFormat) {
      return this.getIsoFormat();
    }

    const javaToUTS35Map = {
      ZZZ: "xxx",
      ZZ: "xx",
      Z: "x",
    };

    // Replace Java pattern tokens with UTS #35 tokens
    let uts35Format = javaFormat;

    // Sort keys by length (descending) to handle multi-character tokens first
    const sortedKeys = Object.keys(javaToUTS35Map).sort(
      (a, b) => b.length - a.length,
    );

    for (const javaToken of sortedKeys) {
      const uts35Token = javaToUTS35Map[javaToken];
      const regex = new RegExp(javaToken, "g"); // Match all occurrences
      uts35Format = uts35Format.replace(regex, uts35Token);
    }

    return uts35Format;
  }

  /**
   */
  getLocale(): typeof nl | typeof enGB {
    const defaultLocale = getEnabledLocales()[0];
    const locale = getCookie("locale") ?? defaultLocale ?? "en";

    if (locale === "nl") {
      return nl;
    }

    return enGB;
  }

  /**
   */
  toDate(date: string | Date, inputFormat: string = this.getIsoFormat()): Date {
    if (date instanceof Date) {
      return date;
    }

    const { weekStartsOn, firstWeekContainsDate } = this.getOptions();

    return parse(date, inputFormat, new Date(), {
      in: this.getDateFnsContext(),
      weekStartsOn,
      firstWeekContainsDate,
    });
  }

  /**
   */
  toFormat(
    date: string | Date,
    targetFormat: string,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    const { weekStartsOn } = this.getOptions();

    const parsedDate = this.toDate(date, inputFormat);

    if (isValid(parsedDate)) {
      return format(parsedDate, targetFormat, {
        in: this.getDateFnsContext(),
        weekStartsOn,
        locale: this.getLocale(),
      });
    }

    return "Invalid Date";
  }

  /**
   */
  toISO(
    date: string | Date,
    sourceFormat: string = this.getIsoFormat(),
  ): string {
    return this.toFormat(date, this.getIsoFormat(), sourceFormat);
  }

  /**
   */
  now(): string {
    return this.toISO(new Date());
  }

  ////////////  VALIDATION

  /**
   */
  hasFormat(date: string, sourceFormat: string): boolean {
    const parsedDate = this.toDate(date, sourceFormat);

    const formattedValue = this.toFormat(parsedDate, sourceFormat);
    if (formattedValue === "Invalid Date") {
      return false;
    }

    const isOutsideRange =
      isBefore(parsedDate, new Date(1000, 1, 1)) ||
      isAfter(parsedDate, new Date(9999, 11, 31));
    if (isOutsideRange) {
      return false;
    }

    return (
      formattedValue.replace(/0/gu, "").toUpperCase() ===
      date.replace(/0/gu, "").toUpperCase()
    );
  }

  /**
   */
  isValid(
    date: string | Date | null | void,
    inputFormat: string = this.getIsoFormat(),
  ): boolean {
    if (!date) {
      return false;
    }

    const parsedDate = this.toDate(date, inputFormat);
    return isValid(parsedDate);
  }

  /**
   */
  isAfter(
    inputDate: string | Date,
    afterISODate: ?string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): boolean {
    return (
      afterISODate != null &&
      isAfter(this.toDate(inputDate, inputFormat), this.toDate(afterISODate))
    );
  }

  /**
   */
  isSameOrAfter(
    inputDate: string | Date,
    afterISODate: ?string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): boolean {
    return (
      afterISODate != null &&
      (this.isAfter(inputDate, afterISODate, inputFormat) ||
        this.isSame(inputDate, afterISODate, inputFormat))
    );
  }

  /**
   */
  isBefore(
    inputDate: string | Date,
    beforeISODate: ?string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): boolean {
    return (
      beforeISODate != null &&
      isBefore(this.toDate(inputDate, inputFormat), this.toDate(beforeISODate))
    );
  }

  /**
   */
  isSameOrBefore(
    inputDate: string | Date,
    beforeISODate: ?string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): boolean {
    return (
      beforeISODate != null &&
      (this.isBefore(inputDate, beforeISODate, inputFormat) ||
        this.isSame(inputDate, beforeISODate, inputFormat))
    );
  }

  /**
   */
  isSame(
    inputDate: string | Date,
    compareDate: ?string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): boolean {
    return (
      compareDate != null &&
      this.toDate(inputDate, inputFormat).getTime() ===
        this.toDate(compareDate).getTime()
    );
  }

  /**
   */
  isSameDay(
    inputDate: string | Date,
    compareDate: string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): boolean {
    return (
      compareDate != null &&
      isSameDay(
        this.toDate(inputDate, inputFormat),
        this.toDate(compareDate, inputFormat),
      )
    );
  }

  /**
   */
  isOther(
    inputDate: string | Date,
    compareDate: ?string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): boolean {
    return !this.isSame(inputDate, compareDate, inputFormat);
  }

  /**
   */
  isWeekend(inputDate: string | Date): boolean {
    const SATURDAY_NUMBER = 6;
    const SUNDAY_NUMBER = 0;
    const weekDay = getDay(this.toDate(inputDate));

    return weekDay === SATURDAY_NUMBER || weekDay === SUNDAY_NUMBER;
  }

  /**
   * Checks if a given local date and time is ambiguous in the specified timezone.
   * Ambiguity happens during the end of daylight saving time (DST) transitions.
   */
  isAmbiguous(
    inputDate: string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): boolean {
    if (inputDate == null || typeof Intl === "undefined") {
      return false;
    }

    const dateTime = this.toDate(inputDate, inputFormat);
    if (!isValid(dateTime)) {
      return false;
    }

    const t = [60, -60, 30, -30];
    const a = t.map((x) => {
      const newDate = add(dateTime, { minutes: x });
      return format(newDate, "HH:mm");
    });
    const time = format(dateTime, "HH:mm");
    return a.includes(time);
  }

  ////////////  CALCULATIONS

  /**
   * @private
   */
  _calculate(
    date: string | Date,
    calculationFn: Function,
    property: string,
    value: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    const parsedDate = this.toDate(date, inputFormat);
    return this.toISO(
      calculationFn(
        parsedDate,
        { [property]: value },
        {
          in: this.getDateFnsContext(),
        },
      ),
    );
  }

  /**
   */
  addSeconds(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, add, "seconds", amount, inputFormat);
  }

  /**
   */
  addMinutes(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, add, "minutes", amount, inputFormat);
  }

  /**
   */
  addHours(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, add, "hours", amount, inputFormat);
  }

  /**
   */
  addDays(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, add, "days", amount, inputFormat);
  }

  /**
   */
  addWeeks(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, add, "weeks", amount, inputFormat);
  }

  /**
   */
  addMonths(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, add, "months", amount, inputFormat);
  }

  /**
   */
  addYears(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, add, "years", amount, inputFormat);
  }

  /**
   */
  subtractSeconds(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, sub, "seconds", amount, inputFormat);
  }

  /**
   */
  subtractMinutes(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, sub, "minutes", amount, inputFormat);
  }

  /**
   */
  subtractHours(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, sub, "hours", amount, inputFormat);
  }

  /**
   */
  subtractDays(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, sub, "days", amount, inputFormat);
  }

  /**
   */
  subtractWeeks(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, sub, "weeks", amount, inputFormat);
  }

  /**
   */
  subtractMonths(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, sub, "months", amount, inputFormat);
  }

  /**
   */
  subtractYears(
    date: string,
    amount: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._calculate(date, sub, "years", amount, inputFormat);
  }

  ////////////  GETTERS

  /**
   * @private
   */
  _get(
    date: string | Date,
    method: Function,
    inputFormat: string = this.getIsoFormat(),
    options: Object = {},
  ): string {
    const parsedDate = this.toDate(date, inputFormat);
    return this.toISO(
      method(parsedDate, {
        in: this.getDateFnsContext(),
        ...options,
      }),
    );
  }

  /**
   */
  startOfMonth(
    date: string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._get(date, startOfMonth, inputFormat);
  }

  /**
   */
  endOfMonth(
    date: string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._get(date, endOfMonth, inputFormat);
  }

  /**
   */
  startOfWeek(
    date: string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    const { weekStartsOn } = this.getOptions();
    return this._get(date, startOfWeek, inputFormat, {
      weekStartsOn,
    });
  }

  /**
   */
  getHours(
    date: string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): number {
    const parsedDate = this.toDate(date, inputFormat);
    return getHours(parsedDate, { in: this.getDateFnsContext() });
  }

  /**
   */
  getMinutes(
    date: string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): number {
    const parsedDate = this.toDate(date, inputFormat);
    return getMinutes(parsedDate, { in: this.getDateFnsContext() });
  }

  /**
   */
  getWeek(
    date: string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): number {
    const { weekStartsOn, firstWeekContainsDate } = this.getOptions();
    const parsedDate = this.toDate(date, inputFormat);
    return getWeek(parsedDate, {
      in: this.getDateFnsContext(),
      weekStartsOn,
      firstWeekContainsDate,
    });
  }

  //////////// SETTERS

  /**
   * @private
   */
  _set(
    date: string | Date,
    property: string,
    value: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    const parsedDate = this.toDate(date, inputFormat);
    return this.toISO(
      set(
        parsedDate,
        { [property]: value },
        // {
        //   in: this.getDateFnsContext(),
        // },
      ),
    );
  }

  /**
   */
  setYear(
    date: string | Date,
    year: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._set(date, "year", year, inputFormat);
  }

  /**
   */
  setMonth(
    date: string | Date,
    month: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._set(date, "month", month, inputFormat);
  }

  /**
   */
  setDay(
    date: string | Date,
    day: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._set(date, "date", day, inputFormat);
  }

  /**
   */
  setHour(
    date: string | Date,
    hour: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._set(date, "hours", hour, inputFormat);
  }

  /**
   */
  setMinute(
    date: string | Date,
    minute: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._set(date, "minutes", minute, inputFormat);
  }

  /**
   */
  setSecond(
    date: string | Date,
    second: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._set(date, "seconds", second, inputFormat);
  }

  /**
   */
  setMilliseconds(
    date: string | Date,
    millisecond: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    return this._set(date, "milliseconds", millisecond, inputFormat);
  }

  /**
   */
  setTime(
    date: string,
    hours: number,
    minutes: number,
    seconds: number,
    milliseconds: number,
    inputFormat: string = this.getIsoFormat(),
  ): string {
    const parsedDate = this.toDate(date, inputFormat);
    return this.toISO(
      set(
        parsedDate,
        { hours, minutes, seconds, milliseconds },
        {
          in: this.getDateFnsContext(),
        },
      ),
    );
  }

  //////////// Timezone related

  /**
   */
  getTimeZone(): string {
    return this._timeZone;
  }

  /**
   */
  setTimeZone(timeZone: string) {
    this._timeZone = timeZone;
  }

  /**
   */
  getDateFnsContext(): null | Function {
    if (this.doOffsets && !IS_GRAALJS) {
      return tz(this.getTimeZone());
    }
    return undefined;
  }

  //////////// Offsets

  /**
   */
  getOffset(
    date: string | Date,
    inputFormat: string = this.getIsoFormat(),
  ): OffsetInfo | null {
    const parsedDate = this.toDate(date, inputFormat);
    if (isValid(parsedDate)) {
      const dateOffset = this.toFormat(parsedDate, DATETIME_OFFSET_FORMAT);
      const offsets = this.getTimezoneOffsets();
      if (offsets.daylight?.value === dateOffset) {
        return offsets.daylight ?? null;
      } else if (offsets.standard?.value === dateOffset) {
        return offsets.standard ?? null;
      }
    }
    return null;
  }

  /**
   */
  getTimezoneOffsets(): {
    standard?: OffsetInfo,
    daylight?: OffsetInfo,
  } {
    const createOffsetObject = (info: OffsetInfoInput): OffsetInfo => {
      const sign = info.offset < 0 ? "-" : "+";
      const absOffset = Math.abs(info.offset);
      const hours = Math.trunc(absOffset);
      const minutes = Math.round((absOffset % 1) * 60);

      const offsetString = `${sign}${String(hours + (minutes === 60 ? 1 : 0)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

      return {
        label: info.name,
        abbr: info.abbr,
        value: offsetString,
      };
    };

    const timezone = soft(this.getTimeZone());

    if (timezone.length === 0) {
      return {
        standard: {
          label: "Etc/UTC",
          abbr: "Etc/UTC",
          value: "+00:00",
        },
      };
    }

    const { daylight, standard } = timezone[0];

    if (daylight) {
      return {
        daylight: createOffsetObject(daylight),
        standard: createOffsetObject(standard),
      };
    }

    return { standard: createOffsetObject(standard) };
  }
}

/**
 * DateUtil
 */
const DateUtil: BaseDateTimeUtil = new BaseDateTimeUtil(ISO_DATE_FORMAT);

/**
 * DateTimeUtil
 */
const DateTimeUtil: BaseDateTimeUtil = new BaseDateTimeUtil(
  ISO_DATETIME_FORMAT,
  true,
);

/**
 * TimeUtil
 */
const TimeUtil: BaseDateTimeUtil = new BaseDateTimeUtil(ISO_TIME_FORMAT);

/**
 * TimestampUtil
 */
const TimestampUtil: BaseDateTimeUtil = new BaseDateTimeUtil(
  ISO_TIMESTAMP_FORMAT,
  true,
);

export { DateUtil, DateTimeUtil, TimeUtil, TimestampUtil };
